import {
  BackgroundProps,
  DisplayProps,
  FontSizeProps,
  FontWeightProps,
  LineHeightProps,
  MinWidthProps,
  SpaceProps,
  background,
  display,
  fontSize,
  fontWeight,
  lineHeight,
  minWidth,
  space,
  width,
  compose,
} from 'styled-system';
import { Flex, Box } from 'components/box';
import FormError from 'components/form/error';
import Label from 'components/_shared/elements/label';
import {
  forwardRef,
  useState,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  useRef,
} from 'react';
import { css } from '@emotion/react';
import styled from 'components/styled';

type Props = SpaceProps &
  FontSizeProps &
  FontWeightProps &
  LineHeightProps &
  BackgroundProps &
  MinWidthProps &
  DisplayProps;

const Input = styled.input<Props>`
  ${({ theme }) => {
    return css`
      outline: none;
      appearance: none;
      display: block;
      width: 100%;
      font-family: ${theme.fonts.body};
      font-size: ${theme.fontSizes[2]};
      color: inherit;
      background-color: transparent;
      border: none;
      margin-bottom: 0;

      &:active,
      &:focus {
        border-color: ${theme.colors.primary};
      }

      ::placeholder {
        color: ${theme.colors.mediumGrey};
      }
      ::-ms-clear {
        display: none;
      }
    `;
  }}
  ${compose(
    space,
    fontSize,
    width,
    fontWeight,
    lineHeight,
    background,
    minWidth,
    display
  )}
`;

export type InputGroupProps = {
  borderRadius?: number;
  label?: string;
  error?: string;
  invalid?: boolean;
  groupPrefix?: ReactNode;
  groupSuffix?: ReactNode;
} & Props &
  InputHTMLAttributes<HTMLInputElement>;

type RefHandler = (ref: HTMLInputElement | null) => void;

export function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T> | undefined | null>
): React.RefCallback<T> {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

const InputGroup = forwardRef(
  (
    {
      borderRadius,
      error,
      id,
      invalid,
      label,
      mb,
      groupPrefix: prefix,
      groupSuffix: suffix,
      ...rest
    }: InputGroupProps,
    ref: Ref<HTMLInputElement> | RefHandler
  ) => {
    const [isFocused, setFocus] = useState(false);
    const htmlId = id || `${rest.name}-${Math.ceil(Math.random() * 1000)}`;

    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <Box>
        {label && (
          <Label
            mb={2}
            htmlFor={htmlId}
            isFocused={isFocused}
            invalid={invalid}
          >
            {label}
          </Label>
        )}
        <Flex
          mb={mb}
          alignItems="center"
          css={theme => css`
            border: 2px solid ${theme.colors.borderColor};
            padding: ${theme.space[2]}px 12px;
            font-family: ${theme.fonts.body};
            font-size: ${theme.fontSizes[2]};
            color: inherit;
            background: white;
            ${isFocused &&
            css`
              border-color: ${theme.colors.primary};
            `}
            ${invalid &&
            css`
              border-color: ${theme.colors.accent};
            `}
            ${borderRadius &&
            css`
              border-radius: ${theme.space[borderRadius]}px;
            `}
          `}
        >
          {prefix && (
            <Box
              pr={2}
              onClick={() => inputRef.current && inputRef.current.focus()}
            >
              {prefix}
            </Box>
          )}
          <Input
            {...rest}
            id={htmlId}
            ref={mergeRefs([ref, inputRef])}
            onFocus={e => {
              rest.onFocus && rest.onFocus(e);
              setFocus(true);
            }}
            onBlur={e => {
              rest.onBlur && rest.onBlur(e);
              setFocus(false);
            }}
          />
          {suffix && (
            <Box
              onClick={() => inputRef.current && inputRef.current.focus()}
              css={css`
                padding-left: 8px;
              `}
            >
              {suffix}
            </Box>
          )}
        </Flex>
        {error && invalid && <FormError>{error}</FormError>}
      </Box>
    );
  }
);

InputGroup.defaultProps = {
  borderRadius: 1,
  type: 'text',
};

export default InputGroup;
